import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import CompanyList from './components/CompanyList';
import { Badge, Counters, Skel, useCompany } from '../../components/app2';
import { authService } from '../../utils/authService';

const CompanyManagement = () => {
  const navigate = useNavigate();
  const { setEmpresaActiva } = useCompany();
  const [companies, setCompanies] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Load user profile with companies and employees data from API
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        setLoadingProfile(true);
        console.log('Loading user profile with companies...');

        const profileData = await authService?.getUserProfile();

        if (profileData) {
          setUserProfile(profileData?.user);

          // Transform API companies data to match component structure
          const transformedCompanies = profileData?.companies?.map(company => ({
            id: company?.id,
            documentId: company?.documentId,
            businessName: company?.name,
            rfc: company?.rfc,
            address: company?.address,
            contactEmail: company?.contact_email,
            contactPhone: company?.contact_phone,
            status: company?.is_active ? 'active' : 'inactive',
            state: company?.state || 'activa', // Campo de estado enum (activa, inactiva, suspendida)
            satPassword: company?.fiel_password,
            employeeCount: company?.employees?.length || 0,
            admins: company?.admins || [],
            adminCount: company?.admins?.length || 0,
            createdAt: company?.createdAt,
            lastSync: company?.sat_last_sync,
            satConnection: company?.sat_last_sync ? 'connected' : 'disconnected',
            employees: company?.employees || [],
            // FIXED: Added logo property to preserve original API structure
            logo: company?.logo || null,
            // Additional fields from API
            updatedAt: company?.updatedAt,
            publishedAt: company?.publishedAt,
            locale: company?.locale
          })) || [];

          setCompanies(transformedCompanies);

          console.log('✅ Profile loaded successfully:', {
            userType: profileData?.user?.user_type,
            companiesCount: transformedCompanies?.length,
            totalEmployees: transformedCompanies?.reduce((total, company) => total + company?.employeeCount, 0),
            totalAdmins: transformedCompanies?.reduce((total, company) => total + company?.adminCount, 0)
          });
        }
      } catch (error) {
        console.error('❌ Error loading user profile:', error);

        // Fallback to mock data if API fails
        console.log('Using fallback mock data...');
        const mockCompanies = [
          {
            id: 1,
            businessName: "Datos no disponibles - Error de API",
            rfc: "API000000XXX",
            address: "Datos no disponibles",
            contactEmail: "no-disponible@example.com",
            contactPhone: "0000000000",
            status: "inactive",
            employeeCount: 0,
            adminCount: 0,
            admins: [],
            satConnection: "disconnected"
          }
        ];
        setCompanies(mockCompanies);
      } finally {
        setLoadingProfile(false);
      }
    };

    loadUserProfile();
  }, []);

  const handleCreateNew = () => {
    navigate('/app/company-form');
  };

  const handleEditCompany = (company) => {
    navigate('/app/company-form', {
      state: {
        company: company,
        mode: 'edit'
      }
    });
  };

  const handleViewCompany = (company) => {
    // "Ver empresa" FIJA la empresa activa de toda la sesión: a partir de aquí,
    // el panel y la sincronización SAT hablan de ella, se entre por los botones
    // o por el menú lateral. El objeto sigue viajando para pintar de inmediato.
    setEmpresaActiva(company?.id);
    navigate(`/app/company-dashboard?companyId=${company?.id}`, {
      state: {
        company: company
      }
    });
  };

  const handleViewAdministrators = (company) => {
    // Entrar a sus administradores también deja esa empresa como la activa.
    setEmpresaActiva(company?.id);
    navigate('/app/company-administrator-management', {
      state: {
        companyId: company?.id,
        companyName: company?.businessName,
        companyRfc: company?.rfc,
        admins: company?.admins || [],
        adminCount: company?.adminCount || 0
      }
    });
  };

  // Get current user data
  const currentUser = authService?.getCurrentUser();
  const userType = currentUser?.user_type || 'company_admin';

  // Contadores globales de la plataforma (los mismos números de siempre,
  // ahora en la barra que propone el diseño en vez de un bloque de texto).
  const apiCaida = !loadingProfile &&
    (companies?.length === 0 || companies?.[0]?.businessName === 'Datos no disponibles - Error de API');

  const contadores = [
    { label: userType === 'super_admin' ? 'Empresas' : 'Empresas asignadas', value: companies?.length ?? 0 },
    { label: 'Activas', value: companies?.filter((c) => c?.state === 'activa')?.length ?? 0, tone: 'ok' },
    { label: 'Empleados', value: companies?.reduce((t, c) => t + (c?.employeeCount || 0), 0) ?? 0 },
    { label: 'Administradores', value: companies?.reduce((t, c) => t + (c?.adminCount || 0), 0) ?? 0 },
  ];

  return (
    <div className="a2-anim-screen">
      <Helmet>
        <title>Gestión de Empresas - PaySlip</title>
        <meta name="description" content="Administre empresas cliente y configure la integración SAT para el procesamiento de nóminas" />
      </Helmet>

      {loadingProfile ? (
        <div className="a2-counters" style={{ marginBottom: 16 }}>
          {contadores.map((c) => (
            <React.Fragment key={c.label}>
              <div><Skel w={54} h={26} /><Skel w={78} h={11} style={{ marginTop: 6 }} /></div>
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 16 }}>
          <Counters items={contadores} />
          {/* El estado de la conexión con la API solo se anuncia cuando falla:
              cuando todo va bien, el dato ya está a la vista en la lista. */}
          {apiCaida ? (
            <Badge tone="danger" icon="cloud_off">Sin conexión con la API</Badge>
          ) : null}
        </div>
      )}

      <CompanyList
        companies={companies}
        selectedCompany={null}
        onSelectCompany={handleEditCompany}
        onViewCompany={handleViewCompany}
        onViewAdministrators={handleViewAdministrators}
        onCreateNew={handleCreateNew}
        isLoading={loadingProfile}
        userType={userType}
      />
    </div>
  );
};

export default CompanyManagement;